import React from "react"
import { View, Pressable, StyleSheet } from "react-native"
import { Image } from "expo-image"
import CompsList from "@/components/analysis/CompsList"
import AnalysisCompsSelected from "@/components/analysis/AnalysisCompsSelected"
import NoCompsInfo from "@/components/analysis/NoCompsInfo"
import { type AnalysisCompType, type AnalysisType } from "@/generated/graphql"
import DealMeterBlock from "@/components/analysis/calculate/DealMeterBlock"
import useGoogleStaticMap from "@/hooks/useGoogleStaticMap"
import { router } from "expo-router"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import Animated from "react-native-reanimated"
import MapExpandIcon from "@/assets/icons/map-expand-icon.svg"
import ToolTips from "@/constants/ToolTips"
import { CopilotStep } from "react-native-copilot"
import { WalkthroughableView } from "@/components/copilot/WalkthroughableComponents"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"

interface CompsTabContentProps {
  analysis: AnalysisType
  onEnterArv: () => void
}

const CompsTabContent: React.FC<CompsTabContentProps> = ({
  analysis,
  onEnterArv,
}) => {
  const hasComps: boolean =
    (analysis?.comps && analysis?.comps.length > 0) || false

  const validComps = (analysis.comps ?? []).filter(
    (comp): comp is AnalysisCompType => comp !== null && comp !== undefined
  )
  const { mapUrl, aspectRatio } = useGoogleStaticMap({
    property: analysis.property,
    comps: validComps,
  })

  // Filter to get only selected and those BF selected comps.

  // const selectedComps = analysis.comps?.filter((comp) => comp?.isSelected) || []
  // const selectedCompsCount: number = selectedComps.length
  /* const allSelectedAreTopComps: boolean = selectedComps.every(
    (comp) =>
      comp?.originalIndex !== null &&
      comp?.originalIndex !== undefined &&
      comp.originalIndex < 4
  ) */
  // was selectedCompsCount === 3 && allSelectedAreTopComps -- in case want to bring it back
  const shouldDisplayIntro: boolean = false

  return (
    <View className="flex flex-1 bg-off-white">
      <DealMeterBlock
        activeTab="Comps"
        analysis={analysis}
        onEnterArv={onEnterArv}
      />
      {hasComps ? (
        <>
          <MapButton
            mapUrl={mapUrl}
            analysisId={analysis.id}
            aspectRatio={aspectRatio}
          />

          <AnalysisCompsSelected
            analysis={analysis}
            displaysIntroductionText={shouldDisplayIntro}
          />

          <CompsList analysis={analysis} />
        </>
      ) : (
        <>
          <NoCompsInfo analysis={analysis} onCalculateSelect={onEnterArv} />
        </>
      )}
    </View>
  )
}

export default CompsTabContent

interface MapButtonProps {
  mapUrl: string
  analysisId: string
  aspectRatio: number
}

const MapButton: React.FC<MapButtonProps> = ({
  mapUrl,
  analysisId,
  aspectRatio,
}) => {
  const { animatedDimStyle } = useUpdateDimming()
  const handlePress = () => {
    router.push(`/properties/${analysisId}/map`)
  }

  const { isFirstManualAnalysisDisplay } = useAnalyzeContext()

  return (
    <Animated.View style={animatedDimStyle}>
      <CopilotStep
        text={ToolTips.copilotStep4CompsMap}
        order={4}
        name="select comps via map"
        active={isFirstManualAnalysisDisplay}
      >
        <WalkthroughableView>
          <Pressable
            testID={`view-map-button`}
            onPress={handlePress}
            className="border-y border-light-grey my-2 mx-0 ios:max-h-[95px] android:max-h-[95px]"
          >
            <View className="relative">
              <Image
                source={{ uri: mapUrl }}
                contentFit="cover"
                style={{
                  width: "100%",
                  height: "100%",
                  aspectRatio,
                }}
              />
              <View
                className="absolute bottom-0 right-1"
                style={styles.customShadow}
              >
                <MapExpandIcon width={42} height={42} />
              </View>
            </View>
          </Pressable>
        </WalkthroughableView>
      </CopilotStep>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  customShadow: {
    shadowColor: "#000",
    shadowOffset: { width: 1, height: 1 },
    shadowOpacity: 0.25,
    shadowRadius: 2,
  },
})
